import _ from 'lodash';


import submenuCeroKm from '../menus/submenu-cerokm';
import LivePage from '../LivePage';
import SpecsGroupSelector from './spec-schema-editor/SpecsGroupSelector';
import ModelContextEditor from '../../components/lego/ModelContextEditor';
import BadgeId from '../../components/common/BadgeId';
import { SpecsJsonTable } from './components/SpecsJsonTable';
import FragmentsPreview from './components/FragmentsPreview';
import LegoContextSummary from '../../components/lego/LegoContextSummary';
import SpecSourceBadge from './components/SpecSourceBadge';
import SpecSourcesList from './components/SpecSourcesList';
import LeftRightLayout from '../../components/common/layout/LeftRightLayout';
import GridLayoutLeftBar from '../../components/common/layout/GridLayoutLeftBar';
import { IconButton } from '../../components/common/IconButton';
import { Icon } from '../../components/common/Icon';
import {
  expandObjectKeys,
  unifySpecs,
  validateRequiredAccordingToSchema,
  validateSpecsAccordingToSchema
} from '../../../../model/cerokm/cerokmUtils.mjs';
import SpecsNoticesTable from './components/SpecsNoticesTable';
import SpecGroupBadge from './components/SpecGroupBadge';


class CeroKmSpecsManager extends LivePage {
  constructor(props) {
    super(props);

    this.svc = this.service('services/cerokm/specs');
    this.svcSpecGroups = this.service('services/cerokm/specsgroup');
    this.state.context = { modelId: 'Toyota-Tacoma', year: 2025 };
    this.state.selectedGroups = this.getUrlParam('group', true);

    this.fullScreen = true;
    this.submenu = submenuCeroKm;
  }

  componentDidMount() {
    if (this.state.selectedGroups?.length) {
      this.refreshSpecs();
    }
  }

  async refreshSpecs({ updateSchema = false, applyTransformations = false } = {}) {
    if(updateSchema || !this.state.schema) {
      const schema = await this.service('services/cerokm/schema').find({query: {$sort: {createdAt: -1}, $limit: 1}});
      this.setState({ schema: schema[0] });
    }

    if (this.state.selectedGroups?.length) {
      this.setState({ loading: true });
      let specGroups = await this.svcSpecGroups.find({ query: { _id: { $in: this.state.selectedGroups } } });

      // Ensure specGroups are sorted like selectedGroups
      specGroups = specGroups.sort((a, b) => this.state.selectedGroups.indexOf(a._id) - this.state.selectedGroups.indexOf(b._id));

      let specs = await Promise.all(this.state.selectedGroups.map((_id) => this.svc.find({ query: { specGroupId: _id } })));

      let { mergedSpecs, specPathToFragmentIds, warnings } = unifySpecs(specs, this.state.schema?.schema);

      if (applyTransformations) {
        await Promise.all(specGroups.map(async (group, index) => {
          const mergedSpec = mergedSpecs[index];
          if (!mergedSpec) return;

          const { warnings: allWarnings, transformedSpecs } = await this.svc.patch(null, {
            specs: specs[index],
          }, {
            query: {
              save: false
            }
          });

          mergedSpecs[index] = transformedSpecs;
          warnings[index] = allWarnings;
        }));
      } else {
        _.each(mergedSpecs, (spec, index) => {
          const validationErrors = validateSpecsAccordingToSchema(expandObjectKeys(spec), this.state.schema?.schema);
          const requiredErrors = validateRequiredAccordingToSchema(spec, this.state.schema?.schema);
          warnings[index].push(...validationErrors);
          warnings[index].push(...requiredErrors);
        });
      }

      // Create missing paths for some warning types so the UI shows the label
      _.each(warnings, (currentWarnings, ix) => {
        const warningsToCheck = _.filter(currentWarnings, w => _.includes(['requiredMissing', 'ruleError'], w.type));
        const currentMergedSpecs = mergedSpecs[ix];
        for (const warning of warningsToCheck) {
          if (!warning.path) {
            // HACK to display warnings/errors without path at the top of the table
            const hackedPath = '_ERRORS_WITHOUTH_PATH';
            warning.path = hackedPath;
          }
          if (_.get(currentMergedSpecs, warning.path) === undefined) {
            _.set(currentMergedSpecs, warning.path, undefined);
          }
        }
      });

      this.setState({ specs, specGroups, mergedSpecs, specPathToFragmentIds, warnings, loading: false });
    }
  }

  onGroupSelected(group) {
    this.setUrlParam('group', group);
    this.setState({ selectedGroups: group }, () => this.refreshSpecs());
  }

  async saveSpecs() {
    if (!this.state.mergedSpecs || !this.state.specGroups) return;

    this.setState({ saving: true });
    try {
      const groupIds = _.map(this.state.specGroups, group => group._id.toString());
      await this.runAsync(this.runServerScript('SaveSpecs', { specsGroupIds: groupIds.join(',')} ), 'Updating affected specs...');
      this.showSuccess('Specs saved successfully');
    } catch (error) {
      console.error('Error saving specs:', error);
      this.showError(`Error saving specs: ${error.message}`);
    } finally {
      this.setState({ saving: false });
    }
  }

  showSuccess(message) {
    this.showNotification({title: message, level: 'success'});
  }

  showError(message) {
    this.showNotification({title: message, level: 'danger'});
  }

  renderPageBody() {
    const { context, selectedGroups, specs, specGroups, loading, mergedSpecs, specPathToFragmentIds, warnings, schema } = this.state;


    const onSpecClick = (fullPath, i, value) => {

      let rows = i === undefined ? _.keys(specPathToFragmentIds) : [i];
      let ids = _.flatMap(rows, (row) => {
        const pathsToIds = specPathToFragmentIds[row];
        let splittedPath = fullPath.split('.');
        let res = [];
        for(const path of _.keys(pathsToIds)) {
          if(path.startsWith(fullPath+'.')) {
            res.push(... (pathsToIds[path] || []));
          }
        }

        while(splittedPath.length) {
          let path = splittedPath.join('.');
          if(pathsToIds[path]) {
            return res.concat(pathsToIds[path]);
          }
          splittedPath.pop();
        }
        return res;
      });

      console.log(fullPath, i, value, ids);

      this.openModal(<FragmentsPreview fragmentIds={ids}/>, {title: <span className='small monospace'>{fullPath}</span>});
    }

    const titles = (specGroups || []).map(g => <div key={g._id} className='text-center'>
      <div>
        <SpecSourceBadge className='' sourceId={g.sourceId} small showContext={false}/>&nbsp;
        <BadgeId id={g._id} />
        <IconButton  href={`/cerokm/fragments?filters=${encodeURIComponent(JSON.stringify({context: {modelId: g.context.modelId}, specsGroupIds: g._id}))}`} target='_blank' icon='open_in_new' small level='primary' title='View fragments'/>      
        <IconButton icon='clear' className='align-middle' level={'danger'} onClick={() => this.onGroupSelected(_.filter(selectedGroups, (id) => id !== g._id))}/>        
      </div>
      <span className='inline-block bg-white rounded'>
        <SpecGroupBadge id={g._id} large/>
      </span>
      <span className='text-right'>
      </span>
    </div>);

    return <GridLayoutLeftBar>
      <div className='px-2' style={{ width: '350px' }}>
        <div className='mx-3 my-1 text-center monospace'>
          Schema <span className='badge badge-dark'>Version {schema?.version}</span>
          <span onClick={() => this.openModal(<SpecsNoticesTable/>, { title: 'Top paths with specs notices'}) }>
            <span className='small ml-2'>Stats</span>
            <IconButton level='danger' icon='gpp_bad' title='View missing stats'/>
          </span>
        </div>

        <div className='d-flex align-items-center'>
          <div className='' style={{ width: '100%' }}>
            <ModelContextEditor value={context} onChange={(ctx) => this.setState({ context: ctx })} />
          </div>
        </div>

        <SpecsGroupSelector context={context} vertical={true} multi={true} onGroupSelected={this.onGroupSelected.bind(this)} initialSelection={selectedGroups} />
      </div>

      <div>
        {specs ? <div className={`${loading ? 'semi-translucent' : ''}`} style={{ minHeight: 'calc(100vh - 20px)' }}>
          <SpecsJsonTable
            specJsons={mergedSpecs}
            warnings={warnings}
            onRefresh={() => this.runAsync(async () => this.refreshSpecs({ updateSchema: true }))}
            onSave={() => this.runAsync(async () => this.saveSpecs())}
            onApplyTransformations={() => this.runAsync(async () => this.refreshSpecs({ updateSchema: true, applyTransformations: true }))}
            onCellClick={onSpecClick}
            titles={titles}
          />
        </div> : null}
      </div>
      {/* <pre>{JSON.stringify(specs, null, 2)}</pre> */}

    </GridLayoutLeftBar>;
  }
}

// for development
export default function Component(props) {
  return <CeroKmSpecsManager {...props} />;
}
